import React, { useEffect, useState } from 'react'

import { startConversation } from '../../api/conversation'
import { createPayment, getPaymentStatus } from '../../api/payments'
import { getSubscriptionInfo } from '../../api/subscription'

import { useApp } from '../../context/AppContext'
import { useSubscription } from '../../context/SubscriptionContext'

import { sendGTMEvent } from '../../utils/analytics'
import { initializeWidget } from '../../utils/cloudpayments-init'
import { CURRENCIES, DOC_TYPE, GA4_KEY, SUBSCRIPTION_MONTH_STARS, SUBSCRIPTION_PERIOD } from '../../utils/constants'
import { formatBotUrl } from '../../utils/common'

import CachedImage from '../CachedImage/CachedImage'
import OffertPopUp from '../OffertPopUp/OffertPopUp'
import Popup from '../PopUp/PopUp'
import SubscriptionLoader from '../SubscriptionLoader/SubscriptionLoader'

import PDFViewer from '../PDFViewer/PDFViewer'

import styles from './SubscriptionMenu.module.css'

const SubscriptionMenu = ({ widget, setIsPaymentOpen }) => {
  const { subscriptionInfo, setSubscriptionInfo, subscriptionHash, setSubscriptionHash } = useSubscription()

  const {
    price,
    user_data: { premium },
  } = subscriptionInfo
  const { appDescription, appSubscriptionInfo, allSubscriptionProducts } = useApp()
  const { bot_url } = appDescription

  const [isLoading, setIsLoading] = useState(false)
  const [openErrorPopup, setOpenErrorPopup] = useState(false)
  const [openOffertPopup, setOpenOffertPopup] = useState(false)
  const [confirmOffert, setConfirmOffert] = useState(false)

  const findProduct = productName => {
    return [...appSubscriptionInfo?.products, ...appSubscriptionInfo?.tokens].find(
      product => product.product_name === productName
    )
  }

  // Обработчик платежа
  const handlePayment = async hash => {
    setIsPaymentOpen(true)

    try {
      let result
      if (appSubscriptionInfo?.currency === '*') {
        result = await new Promise(resolve => {
          window.Telegram.WebApp.openInvoice(hash.url, async status => {
            switch (status) {
              case 'paid':
                resolve('closed')
                break
              case 'cancelled':
                resolve('cancelled')
                break
              case 'failed':
                resolve('failed')
                break
              case 'pending':
                resolve('pending')
                break
              default:
                resolve('failed')
            }
          })
        })
      } else {
        result = await initializeWidget(widget, price, hash.hash, setIsLoading)
      }
      if (result.success) {
        const status = await getPaymentStatus(hash.hash)

        if (status.status === 'closed') {
          const subscription = await getSubscriptionInfo()
          setSubscriptionInfo(subscription)
        } else {
          setOpenErrorPopup(true)
        }
      }
    } catch (error) {
      console.error('Payment error:', error)
      setOpenErrorPopup(true)
    } finally {
      setSubscriptionHash(null)
      setIsLoading(false)
      setIsPaymentOpen(false)
      setConfirmOffert(false)
    }
  }

  // Эффект для обработки существующего хэша
  useEffect(() => {
    if (subscriptionHash && confirmOffert) {
      handlePayment(subscriptionHash)
    }
    // eslint-disable-next-line
  }, [subscriptionHash, confirmOffert])

  // Обработчик клика по кнопке подписки
  const handleSubscriptionClick = async period => {
    window.Telegram.WebApp.HapticFeedback.impactOccurred('light')

    if (premium) {
      sendGTMEvent(GA4_KEY.manage_subscription)
    } else {
      try {
        setOpenOffertPopup(true)
        sendGTMEvent(GA4_KEY.buy_subscription)

        const orderDetails = await createPayment(
          appSubscriptionInfo?.currency === '*' ? period || SUBSCRIPTION_MONTH_STARS : SUBSCRIPTION_PERIOD
        )

        setSubscriptionHash(orderDetails)
      } catch (error) {
        setOpenOffertPopup(false)
        console.error('Payment creation error:', error)
        setOpenErrorPopup(true)
      }
    }
  }

  return (
    <div className={styles.subscriptionContainer}>
      {isLoading && <SubscriptionLoader />}
      {appSubscriptionInfo?.logo && (
        <div className={styles.subscriptionHeader}>
          <CachedImage src={appSubscriptionInfo?.logo} className={styles.logoBack} alt='Фон подписки' />
        </div>
      )}
      <div className={styles.subscriptionContent}>
        <PDFViewer pdfUrl={appSubscriptionInfo?.subscribeText} />
      </div>

      <div className={styles.subscriptionFooter}>
        {premium ? (
          <button className={styles.subscribeButton} onClick={handleSubscriptionClick}>
            Управление подпиской
          </button>
        ) : window.APP_ID === 1 ? (
          <button className={styles.subscribeButton} onClick={handleSubscriptionClick}>
            {appSubscriptionInfo?.buttonText || 'Подписаться за'} {price} {CURRENCIES[appSubscriptionInfo?.currency]}
          </button>
        ) : (
          <>
            {allSubscriptionProducts?.map(product => {
              if (!product.enabled) {
                return null
              }
              const productInfo = findProduct(product.product_name)
              return (
                <button className={styles.subscribeButton} onClick={() => handleSubscriptionClick(product.product_name)}>
                  {productInfo?.text} {product?.price}
                  {CURRENCIES[appSubscriptionInfo?.currency]}{' '}
                  {productInfo?.period
                    ? productInfo?.period === 'year'
                      ? 'на год'
                      : productInfo?.period === 'month'
                      ? 'на месяц'
                      : productInfo?.period === 'week'
                      ? 'на неделю'
                      : 'на день'
                    : ''}
                </button>
              )
            })}
          </>
        )}

        <button
          className={styles.chatButton}
          onClick={() => {
            const tg = window.Telegram?.WebApp
            if (tg) {
              const isTdesktop = tg.platform === 'tdesktop'

              window.Telegram.WebApp.setHeaderColor('#1D2733')
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
              startConversation('Привет! Рассказажи мне про подписку!')

              if (!tg.initDataUnsafe?.query_id) {
                tg.openTelegramLink(formatBotUrl(bot_url))
              }

              document.querySelector('.App').classList.add('fadeOut')
              sendGTMEvent(GA4_KEY.open_ai_chat)
              if (isTdesktop) {
                setTimeout(() => {
                  tg.close()
                }, 350)
              } else {
                tg.close()
              }
            }
          }}
        >
          Задать вопрос в чате
        </button>
      </div>
      <OffertPopUp
        isOpen={openOffertPopup}
        onClose={() => setOpenOffertPopup(false)}
        type={DOC_TYPE.extra_func}
        needConfirm
        onConfirm={() => {
          setConfirmOffert(true)
          setOpenOffertPopup(false)
        }}
      />

      <Popup isOpen={openErrorPopup} onClose={() => setOpenErrorPopup(false)}>
        <h2 className={styles.errorPopupTitle}>
          Оплата прошла неудачно
          <br />
          или вы закрыли окно оплаты
        </h2>
        <p className={styles.errorPopupDescription}>Пожалуйста, попробуйте еще раз или обратитесь в службу поддержки</p>
        <div className={styles.errorPopupButtons}>
          <button
            className={styles.keepButton}
            onClick={() => {
              const tg = window.Telegram?.WebApp
              const isTdesktop = tg.platform === 'tdesktop'

              window.Telegram.WebApp.setHeaderColor('#1D2733')
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
              startConversation('Привет! У меня проблемы с оплатой!')
              if (!tg?.initDataUnsafe?.query_id) {
                tg.openTelegramLink(formatBotUrl(bot_url))
              }
              document.querySelector('.App').classList.add('fadeOut')
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
              setOpenErrorPopup(false)
              sendGTMEvent(GA4_KEY.open_ai_chat)
              if (isTdesktop) {
                setTimeout(() => {
                  tg.close()
                }, 350)
              } else {
                tg.close()
              }
            }}
          >
            Обратиться в службу поддержки
          </button>
          <button
            className={styles.cancelButton}
            onClick={() => {
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
              setOpenErrorPopup(false)
            }}
          >
            Закрыть
          </button>
        </div>
      </Popup>
    </div>
  )
}

export default SubscriptionMenu
